import { FormLabel } from '@mui/material';
import { Formik } from 'formik';
import { useState } from 'react';
import { FileWithPath } from 'react-dropzone/.';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { LinearProgress } from 'src/components/LinearProgress';
import { Link } from 'src/components/Link';
import { Meta } from 'src/components/Meta';
import { RadioGroup } from 'src/components/RadioGroup';
import { StickyCard } from 'src/components/StickyCard';
import { Typography } from 'src/components/Typography';
import { InlineFileUploader } from 'src/components/Uploader/InlineFileUploader';
import { useSession } from 'src/contexts/SessionContext';
import { GroupResource, Resource } from 'src/models/resource/_types';
import {
  createStandaloneResource,
  fetchGroupResource,
  fetchResources,
  handleResourceFileUpload,
  newGroupResource,
  updateGroupResource,
} from 'src/models/resource/resource';
import { fetchVideo } from 'src/models/video/video';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';
import { FormTagsSelect } from 'src/shared/Forms/Selects/TagsSelect';
import { Scheduling } from 'src/shared/Scheduling';
import { Identifier } from 'src/types/utilityTypes';
import {
  handleFileSelect,
  handleVideoUploadWithProcessing,
} from 'src/utils/fileHandlers';
import toast from 'src/utils/toast';
import * as Yup from 'yup';

export const NewGroupResourcePage = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { resourceId } = useParams<{ resourceId: string }>();
  const { t } = useTranslation('translation', { keyPrefix: 'group.resource' });
  const { t: tButtons } = useTranslation('translation', {
    keyPrefix: 'common.buttons',
  });
  const { session } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedVideoId, setUploadedVideoId] = useState<
    Identifier | undefined
  >(undefined);

  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);

  const [inputType, setInputType] = useState<'file' | 'url' | 'existing'>(
    'url'
  );

  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null
  );

  const { data: resourcesOptions = [] } = useQuery({
    queryKey: ['resources'],
    queryFn: async () => {
      const response = await fetchResources({ fetchAll: true });
      return response || [];
    },
    staleTime: 60000,
    cacheTime: 60000,
  });

  const { data: uploadedVideo, isLoading: loadingVideo } = useQuery({
    queryKey: ['video', uploadedVideoId],
    queryFn: () => fetchVideo({ id: uploadedVideoId as Identifier }),
    enabled: !!uploadedVideoId,
  });

  const { data: groupResource } = useQuery({
    queryKey: ['groupResource', resourceId],
    queryFn: () =>
      fetchGroupResource({ groupResourceId: resourceId as Identifier }),
    enabled: !!resourceId && resourceId !== 'new',
  });

  const createThumbnailMutation = useMutation({
    mutationFn: (file: FileWithPath) => handleResourceFileUpload(file),
    onError: () => {
      toast.error(t('Failed to upload thumbnail'));
    },
  });

  const createGroupResourceMutation = useMutation({
    mutationFn: (data: Partial<GroupResource>) => newGroupResource({ data }),
    onSuccess: () => {
      toast.success(t('Group resource created successfully'));
      queryClient.invalidateQueries(['groupResources']);
      navigate(`/groups/${groupId}/resources`, { replace: true });
    },
    onError: () => {
      toast.error(t('Failed to create group resource'));
    },
  });

  const updateGroupResourceMutation = useMutation({
    mutationFn: (data: Partial<GroupResource>) =>
      updateGroupResource({ data, groupResourceId: resourceId as Identifier }),
    onSuccess: () => {
      toast.success(t('Group resource updated successfully'));
      queryClient.invalidateQueries(['groupResources']);
      navigate(`/groups/${groupId}/resources`, { replace: true });
    },
    onError: () => {
      toast.error(t('Failed to update group resource'));
    },
  });

  const handleResourceChange = async (
    resource: Resource | undefined,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (!resource) return;

    setFieldValue('name', resource.name);
    setFieldValue('tags', resource.tags || []);
    setFieldValue('thumbnail', resource.thumbnail);
    setFieldValue('largeThumbnail', resource.thumbnail);
    setFieldValue('thumbnailId', resource.thumbnailId);
    setFieldValue('link', resource.resourceUrl || resource.fileUrl);
  };

  const formikProps = {
    initialValues: {
      groupId: groupId,
      name: groupResource?.name || '',
      tags: groupResource?.tags || [],
      embedUrl: groupResource?.embedUrl || '',
      thumbnail: groupResource?.thumbnail || '',
      largeThumbnail: groupResource?.largeThumbnail || '',
      link: groupResource?.link || '',
      delayInterval: groupResource?.delayInterval || '',
      expiresAt: groupResource?.expiresAt || '',
      resourceId: groupResource?.resourceId || null,
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required(t("Name can't be blank")),
    }),
    enableReinitialize: true,
    onSubmit: (values) => {
      if (resourceId === 'new') {
        createGroupResourceMutation.mutateAsync(values);
      } else {
        updateGroupResourceMutation.mutateAsync(values);
      }
    },
  };

  return (
    <Formik {...formikProps}>
      {({ values, submitForm, setFieldValue }) => (
        <>
          <AppFrameConfig
            id="groupResource"
            containerMaxWidth={'xl'}
            backButtonTitle={'resources'}
            backButtonLinkTo={`/groups/${groupId}/resources`}
            title={
              resourceId === 'new'
                ? t('New Group Resource')
                : t('Editing Group Resource')
            }
          />
          <StickyCard
            title={
              resourceId === 'new'
                ? t('New Group Resource')
                : t('Editing Group Resource')
            }
            actions={
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  loading={
                    createGroupResourceMutation.isLoading ||
                    updateGroupResourceMutation.isLoading
                  }
                  onClick={() => {
                    submitForm();
                  }}
                >
                  {tButtons('save')}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    navigate(`/groups/${groupId}/resources`, {
                      replace: true,
                    });
                  }}
                >
                  {tButtons('cancel')}
                </Button>
              </Box>
            }
          >
            <Meta
              title={
                resourceId === 'new'
                  ? t('New Group Resource')
                  : t('Editing Group Resource')
              }
              brand={session?.brand}
            />
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
            >
              <FormTextField name="name" label={t('Name')} fullWidth required />
              <FormTagsSelect
                name="tags"
                label={t('Tags (Optional)')}
                tagType="groupResource"
                multiple
              />
              <RadioGroup
                options={[
                  { label: t('Upload File'), value: 'file' },
                  { label: t('Enter URL'), value: 'url' },
                  { label: t('Use Existing Resource'), value: 'existing' },
                ]}
                label=""
                value={inputType}
                onChange={(e) =>
                  setInputType(e.target.value as 'file' | 'url' | 'existing')
                }
              />

              {inputType === 'file' ? (
                <>
                  <InlineFileUploader
                    accept={{
                      'image/*': [],
                      'application/pdf': [],
                      'application/vnd.ms-excel': [],
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                        [],
                      'video/*': [],
                    }}
                    maxFileSize={32 * 1024 * 1024}
                    maxFileSizeCaption={t('Max file size is 32 MB')}
                    onFileSelect={(files) => {
                      if (!files) {
                        setFieldValue('link', null);
                        setFieldValue('resourceId', null);
                        setUploadedVideoId(undefined);
                        return;
                      }
                      handleFileSelect({
                        selectedFiles: files ? [files] : [],
                        setUploadingState: setUploading,
                        setProgressState: setUploadProgress,
                        setAssetState: () => void 0,
                        setUploadedVideoId: (id) => {
                          setFieldValue('resourceId', id);
                          setUploadedVideoId(id);
                        },
                        handleVideoUploadFunc: async (file, cb) => {
                          const res = await handleVideoUploadWithProcessing(
                            file,
                            {
                              onUploadProgress: cb,
                            }
                          );
                          setFieldValue('link', res.streamUrl);
                          return {
                            videoId: res.videoId,
                          } as any;
                        },
                        handleUpload: async (file) => {
                          const result = await createStandaloneResource(file);
                          setFieldValue('link', result.file_url);
                          setFieldValue('resourceId', result.id);
                          return result;
                        },
                      });
                    }}
                    multiple={false}
                  />
                  {uploading && (
                    <Box sx={{ width: '100%' }}>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                      >
                        {uploadProgress === 100
                          ? t('Upload complete')
                          : `${Math.round(uploadProgress)}% ${t(
                              'Uploaded'
                            ).toLowerCase()}`}
                      </Typography>
                    </Box>
                  )}
                  {loadingVideo && (
                    <Typography>{t('Loading resource data')}</Typography>
                  )}
                  {uploadedVideo && uploadedVideo.name && (
                    <Typography variant="body2">
                      {t('Uploaded')} {uploadedVideo.name}
                    </Typography>
                  )}
                </>
              ) : inputType === 'url' ? (
                <FormTextField
                  name="link"
                  label={t('Resource URL')}
                  fullWidth
                />
              ) : inputType === 'existing' ? (
                <BaseSelect
                  label={t('Select Resource')}
                  value={selectedResource as any}
                  options={resourcesOptions.map((resource) => ({
                    label: resource.name,
                    value: resource,
                    id: resource.id,
                  }))}
                  valueOnly={false}
                  onChange={(resource) => {
                    setSelectedResource(resource.value);
                    handleResourceChange(resource.value, setFieldValue);
                  }}
                />
              ) : null}
              {values.link && (
                <Typography variant="body2">
                  {t('Current Resource')}:{' '}
                  <Box
                    component={Link}
                    href={values.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {values.name || values.link}
                  </Box>
                </Typography>
              )}
              <Scheduling optional type={t('one').toLowerCase()} />
              <Box>
                <FormLabel>{t('Resource Thumbnail (optional)')}</FormLabel>
                <InlineFileUploader
                  accept={{ 'image/*': [] }}
                  maxFileSize={32 * 1024 * 1024}
                  maxFileSizeCaption={
                    <HtmlTypography>
                      {t('max file size caption')}
                    </HtmlTypography>
                  }
                  onFileSelect={async (files) => {
                    if (!files) return;
                    handleFileSelect({
                      selectedFiles: files ? [files] : [],
                      setUploadingState: setThumbnailUploading,
                      setProgressState: setThumbnailUploadProgress,
                      handleUpload: (file) =>
                        createThumbnailMutation.mutateAsync(file),
                      setAssetState: (result) => {
                        setFieldValue('thumbnail', result.url);
                        setFieldValue('thumbnailId', result.id);
                        setFieldValue('largeThumbnail', result.url);
                      },
                    });
                  }}
                  multiple={false}
                />
                {thumbnailUploading && (
                  <Box sx={{ width: '100%' }}>
                    <LinearProgress
                      variant="determinate"
                      value={thumbnailUploadProgress}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                    >
                      {thumbnailUploadProgress === 100
                        ? t('Upload complete')
                        : `${Math.round(thumbnailUploadProgress)}% ${t(
                            'Uploaded'
                          ).toLowerCase()}`}
                    </Typography>
                  </Box>
                )}
              </Box>
              {values.largeThumbnail && (
                <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                  <FormLabel>{t('Current Thumbnail')}</FormLabel>
                  <img
                    src={values.largeThumbnail}
                    alt={t('Thumbnail')}
                    width="200px"
                  />
                </Box>
              )}
            </Box>
          </StickyCard>
        </>
      )}
    </Formik>
  );
};
